import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from '../../../../angular-material.module';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../../environments/environment';

export interface EditRequestData {
  issue_id: number;
  user_id: number;
  role_id: number;
  issue_category_id: number;
  sub_issue_category_id?: number | null;
  status: number;
  priority_id: number;
  assigned_to: number;
  description: string;
  estimated_cost: number;
  is_approved: boolean;
}

@Component({
  selector: 'app-edit-request',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AngularMaterialModule,
    CommonModule,
  ],
  templateUrl: './edit-request.component.html',
  styleUrls: ['./edit-request.component.scss'],
})
export class EditRequestComponent implements OnInit {
  statusList: any[] = [];
  categoryList: any[] = [];
  subCategoryList: any[] = [];
  showSubCategory = false;
  loadingSubCategories = false;
  roleList: any[] = [];
  allMembers: any[] = [];
  priorityList: any[] = [];
  baseUrl = environment.baseUrl;
  accountID = sessionStorage.getItem('account_id');
  userID = sessionStorage.getItem('session_id');
  roleID = sessionStorage.getItem('role_id');
  roleName = sessionStorage.getItem('role_name');
  requestData: any;
  originalIssueData: any; // Store original data from API for comparison
  loading = false;
  submitting = false;

  get isAdmin(): boolean {
    const roleIdStr = this.roleID || sessionStorage.getItem('role_id');
    const roleIdNum = roleIdStr ? Number(roleIdStr) : NaN;
    const roleNameStr = String(this.roleName || sessionStorage.getItem('role_name') || '').toLowerCase().trim();

    return (
      roleIdNum === 1 ||
      roleIdNum === 12 ||
      roleNameStr.includes('admin')
    );
  }

  editRequestForm = new FormGroup({
    issue_id: new FormControl(this.data?.issue_id ?? this.data?.item?.issue_id),
    user_id: new FormControl(this.data?.user_id ?? this.data?.item?.user_id),
    role_id: new FormControl(this.data?.role_id ?? this.data?.item?.role_id),
    issue_category_id: new FormControl(this.data?.issue_category_id ?? this.data?.item?.issue_category_id),
    issue_sub_category_id: new FormControl(this.data?.issue_sub_category_id ?? this.data?.sub_issue_category_id ?? this.data?.item?.issue_sub_category_id ?? this.data?.item?.sub_issue_category_id ?? null),
    sub_issue_category_id: new FormControl(this.data?.issue_sub_category_id ?? this.data?.sub_issue_category_id ?? this.data?.item?.issue_sub_category_id ?? this.data?.item?.sub_issue_category_id ?? null),
    status: new FormControl(this.data?.status ?? this.data?.item?.status),
    priority_id: new FormControl(this.data?.priority_id ?? this.data?.item?.priority_id),
    assigned_to: new FormControl(this.data?.assigned_to ?? this.data?.item?.assigned_to),
    description: new FormControl(this.data?.description ?? this.data?.item?.description),
    estimated_cost: new FormControl(this.data?.estimated_cost ?? this.data?.item?.estimated_cost ?? null),
    is_approved: new FormControl(this.data?.is_approved ?? this.data?.item?.is_approved ?? false),
  });

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<EditRequestComponent>
  ) {
    this.requestData = data;
  }

  ngOnInit(): void {
    if (this.data && this.data.item) {
      // Get account_id from item or session storage
      const itemAccountId = this.data.item.account_id || this.accountID;
      if (itemAccountId) {
        this.accountID = itemAccountId;
      }

      // Fetch dropdowns first
      this.fetchAllStatus();
      this.allCategoryDropdown();
      this.fetchAllPriority();
      this.fetchAllRoles();

      // Fetch single issue data
      this.fetchSingleIssue();

      // fetch members based on selected role
      this.editRequestForm.get('role_id')?.valueChanges.subscribe((roleId) => {
        if (roleId) {
          this.fetchStaffByRole(roleId);
        } else {
          this.allMembers = [];
        }
      });

      // fetch subcategories based on selected category when changed by user
      this.editRequestForm.get('issue_category_id')?.valueChanges.subscribe((categoryId) => {
        if (!this.loading) {
          this.onCategoryChange(categoryId);
        }
      });
    }
  }

  initializeForm(item: any) {
    if (!item) return;

    // Check if the issue has a parent category vs subcategory
    let parentCatId = item.parent_category_id ?? null;
    let subCatId = item.sub_issue_category_id ?? item.issue_sub_category_id ?? item.sub_category_id ?? item.subcategory_id ?? null;

    if (!parentCatId) {
      // If parent_category_id is not directly on item, check if item.issue_category_id is in categoryList
      const isParent = this.categoryList.some(
        (c: any) => Number(c.issue_category_id) === Number(item.issue_category_id)
      );

      if (isParent) {
        parentCatId = item.issue_category_id;
      } else if (item.issue_category_id != null) {
        // If not in categoryList, item.issue_category_id might be a subcategory ID
        subCatId = subCatId ?? item.issue_category_id;
      }
    } else {
      subCatId = subCatId ?? item.issue_category_id;
    }

    const effectiveCatId = parentCatId ?? item.issue_category_id;

    this.editRequestForm.patchValue({
      issue_id: item.issue_id,
      user_id: item.user_id,
      role_id: item.assigned_to_role_id ?? item.role_id,
      issue_category_id: effectiveCatId,
      issue_sub_category_id: subCatId,
      sub_issue_category_id: subCatId,
      status: item.status,
      priority_id: item.priority_id,
      assigned_to: item.assigned_to,
      description: item.description,
      estimated_cost: item.estimated_cost,
      is_approved: item.is_approved,
    }, { emitEvent: false });

    // Fetch subcategories for the parent category and select the subcategory
    if (effectiveCatId) {
      this.fetchSubCategoriesForInitial(
        effectiveCatId,
        subCatId,
        item.sub_category_name || item.sub_category
      );
    } else {
      this.subCategoryList = [];
      this.showSubCategory = false;
    }

    this.configureCategoryRoleMemberAccess();

    const roleId = item.assigned_to_role_id ?? item.role_id;
    if (roleId) {
      this.fetchStaffByRole(roleId);
    }
  }

  onCategoryChange(categoryId: any): void {
    this.editRequestForm.get('issue_sub_category_id')?.reset(null, { emitEvent: false });
    this.editRequestForm.get('sub_issue_category_id')?.reset(null, { emitEvent: false });
    this.subCategoryList = [];
    this.showSubCategory = false;

    if (!categoryId) {
      return;
    }

    this.loadingSubCategories = true;
    this.http.post(`${this.baseUrl}/subcategory_dropdown`, { issue_category_id: categoryId }).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        this.subCategoryList = list;
        this.showSubCategory = list.length > 0;
        this.loadingSubCategories = false;
        this.configureCategoryRoleMemberAccess();
      },
      error: (err: any) => {
        console.error('Error fetching subcategories:', err);
        this.subCategoryList = [];
        this.showSubCategory = false;
        this.loadingSubCategories = false;
      },
    });
  }

  fetchSubCategoriesForInitial(categoryId: any, targetSubCatId: any, subCategoryName?: string): void {
    if (!categoryId) {
      this.subCategoryList = [];
      this.showSubCategory = false;
      return;
    }

    this.loadingSubCategories = true;
    this.http.post(`${this.baseUrl}/subcategory_dropdown`, { issue_category_id: categoryId }).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        this.subCategoryList = list;
        this.showSubCategory = list.length > 0;
        this.loadingSubCategories = false;

        if (list.length > 0) {
          let matchedSubCat = null;
          if (targetSubCatId != null) {
            matchedSubCat = list.find((s: any) => Number(s.issue_category_id) === Number(targetSubCatId));
          }
          if (!matchedSubCat && subCategoryName) {
            matchedSubCat = list.find(
              (s: any) => String(s.issue_category).toLowerCase().trim() === String(subCategoryName).toLowerCase().trim()
            );
          }

          const selectedId = matchedSubCat ? matchedSubCat.issue_category_id : targetSubCatId;
          if (selectedId != null) {
            this.editRequestForm.patchValue(
              {
                issue_sub_category_id: selectedId,
                sub_issue_category_id: selectedId,
              },
              { emitEvent: false }
            );
          }
        } else {
          this.editRequestForm.patchValue({
            issue_sub_category_id: null,
            sub_issue_category_id: null,
          }, { emitEvent: false });
        }

        this.configureCategoryRoleMemberAccess();
      },
      error: (err: any) => {
        console.error('Error fetching subcategories:', err);
        this.subCategoryList = [];
        this.showSubCategory = false;
        this.loadingSubCategories = false;
      },
    });
  }

  private configureCategoryRoleMemberAccess(): void {
    const lockableFields = ['issue_category_id', 'issue_sub_category_id', 'sub_issue_category_id', 'role_id', 'assigned_to'] as const;
    if (this.isAdmin) {
      lockableFields.forEach((field) =>
        this.editRequestForm.get(field)?.enable({ emitEvent: false })
      );
    } else {
      lockableFields.forEach((field) =>
        this.editRequestForm.get(field)?.disable({ emitEvent: false })
      );
    }
  }

  fetchAllStatus() {
    this.http.get(`${this.baseUrl}/status_dropdown`).subscribe({
      next: (res: any) => (this.statusList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch statuses'),
    });
  }

  allCategoryDropdown() {
    const accountGroupId = this.getAccountGroupId(this.accountID);
    if (accountGroupId == null) {
      this.categoryList = [];
      return;
    }
    this.http.post(`${this.baseUrl}/category_dropdown`, { account_group_id: accountGroupId }).subscribe({
      next: (res: any) => {
        this.categoryList = Array.isArray(res) ? res : [];
        if (this.originalIssueData && !this.showSubCategory && !this.loadingSubCategories) {
          const currentCat = this.editRequestForm.get('issue_category_id')?.value;
          const currentSub = this.editRequestForm.get('sub_issue_category_id')?.value;
          if (!currentSub && currentCat) {
            this.fetchSubCategoriesForInitial(
              currentCat,
              this.originalIssueData.sub_issue_category_id,
              this.originalIssueData.sub_category_name
            );
          }
        }
      },
      error: (err: any) => this.handleError(err, 'Failed to fetch categories'),
    });
  }

  private getAccountGroupId(accountId: string | number | null | undefined): number | null {
    const fromItem = this.data?.item?.account_group_id ?? this.originalIssueData?.account_group_id;
    if (fromItem != null && Number(fromItem) > 0) {
      return Number(fromItem);
    }

    const raw = sessionStorage.getItem('accounts');
    if (!raw || accountId == null) {
      return null;
    }

    try {
      const accounts = JSON.parse(raw);
      if (!Array.isArray(accounts)) {
        return null;
      }
      const match = accounts.find(
        (a: { account_id?: number | string; account_group_id?: number }) =>
          Number(a.account_id) === Number(accountId)
      );
      const groupId = match?.account_group_id;
      return groupId != null && Number(groupId) > 0 ? Number(groupId) : null;
    } catch {
      return null;
    }
  }

  fetchAllPriority() {
    this.http.get(`${this.baseUrl}/priority_dropdown`).subscribe({
      next: (res: any) => (this.priorityList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch priorities'),
    });
  }

  fetchAllRoles() {
    const payload = { account_id: this.accountID };
    this.http.get(`${this.baseUrl}/role_dropdown`).subscribe({
      next: (res: any) => (this.roleList = res),
      error: (err: any) => this.handleError(err, 'Failed to fetch roles'),
    });
  }

  fetchSingleIssue() {
    if (!this.data || !this.data.item) {
      return;
    }

    const item = this.data.item;
    const accountId = item.account_id || this.accountID;
    const issueId = item.issue_id;
    const userId = item.user_id || this.userID;

    if (!accountId || !issueId || !userId) {
      this.snackBar.open('Missing required data to fetch issue details.', 'Close', { duration: 3000 });
      return;
    }

    this.loading = true;
    const payload = {
      account_id: accountId,
      issue_id: issueId,
      user_id: userId,
    };

    this.http.post(`${this.baseUrl}/fetch_single_issue`, payload).subscribe({
      next: (res: any) => {
        const issueData = (res && typeof res === 'object' && res.data) ? res.data : (Array.isArray(res) ? res[0] : res);
        this.originalIssueData = issueData;
        this.requestData = { item: issueData };

        // Initialize form with fetched data
        this.initializeForm(issueData);

        // Refresh categories with account_group_id from fetched issue if available
        this.allCategoryDropdown();
      },
      error: (err: any) => {
        console.error('Error fetching single issue:', err);
        this.handleError(err, 'Failed to fetch issue details');
        // Fallback to using data.item if API fails
        this.initializeForm(this.data.item);
        this.originalIssueData = this.data.item;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  fetchStaffByRole(roleId: any) {
    const accountId = this.accountID || sessionStorage.getItem('account_id');
    if (!roleId || !accountId) {
      this.allMembers = [];
      return;
    }

    const payload = {
      account_id: accountId,
      role_id: roleId,
    };

    this.http.post(`${this.baseUrl}/assign_project_users_dropdown`, payload).subscribe({
      next: (res: any) => {
        const staff = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
        this.allMembers = staff.map((m: any) => {
          const fullName = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim();
          return {
            ...m,
            user_id: m.user_id || m.id,
            member_name: fullName || m.name || m.user_name || 'Unnamed User',
          };
        });
      },
      error: (err: any) => this.handleError(err, 'Failed to fetch members'),
    });
  }

  handleError(error: any, message: string) {
    console.error(message, error);
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  closeDialog() {
    this.dialogRef.close(false);
  }

  onSubmit() {
    if (!this.editRequestForm.valid) {
      this.snackBar.open('Please fill all required fields correctly.', 'Close', { duration: 3000 });
      return;
    }

    const formValue = this.editRequestForm.getRawValue();
    const subCatId = formValue.issue_sub_category_id || formValue.sub_issue_category_id || null;
    const finalCategoryId = subCatId || formValue.issue_category_id;

    const payload: { [key: string]: any } = {
      issue_id: formValue.issue_id,
      role_id: formValue.role_id,
      account_id: this.accountID,
      user_id: formValue.user_id,
      assigned_by: this.userID,
      issue_category_id: finalCategoryId,
      issue_sub_category_id: subCatId,
      sub_issue_category_id: subCatId,
    };

    // List of editable fields to track
    const fieldsToCheck = [
      'status',
      'priority_id',
      'assigned_to',
      'description',
      'estimated_cost',
      'is_approved',
    ] as const;

    let isChanged = false;

    // Compare form values with original data and add to payload if changed
    const originalData = this.originalIssueData || this.data?.item || {};
    const originalSubCat = originalData.issue_sub_category_id ?? originalData.sub_issue_category_id ?? null;
    const originalCat = originalData.parent_category_id ?? originalData.issue_category_id ?? null;

    if (
      String(finalCategoryId ?? '') !== String(originalData.issue_category_id ?? '') ||
      String(subCatId ?? '') !== String(originalSubCat ?? '') ||
      String(formValue.issue_category_id ?? '') !== String(originalCat ?? '')
    ) {
      isChanged = true;
    }

    fieldsToCheck.forEach((field) => {
      const key = field as keyof typeof payload;
      const newValue = formValue[field];
      const oldValue = originalData[field as keyof typeof originalData];

      payload[key] = newValue;

      if (newValue !== oldValue) {
        isChanged = true;
      }
    });

    if (!isChanged) {
      this.snackBar.open('No changes detected to save.', 'Close', { duration: 3000 });
      return;
    }

    // Submit payload to backend
    this.submitting = true;
    this.http.post(`${this.baseUrl}/assign_task`, payload).subscribe({
      next: (response) => {
        this.snackBar.open('Issue updated successfully!', 'Close', { duration: 3000 });
        this.dialogRef.close(response);
      },
      error: (err) => {
        console.error('Error updating issue:', err);
        this.snackBar.open('Failed to update issue, please try again later.', 'Close', { duration: 3000 });
      },
      complete: () => {
        this.submitting = false;
      },
    });
  }
}
